/**
 * ApplicationSelector
 * 
 * Dropdown component for selecting a Forge Application to map to a UseCase.
 */
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import forgeService from '../../services/forgeService';
import { ForgeApplication } from '../../types';

interface ApplicationSelectorProps {
  forgeId?: string | null;  // Filter applications by forge ID
  selectedApplicationId?: string | null;
  onSelect: (applicationId: string | null, application: ForgeApplication | null) => void;
  disabled?: boolean;
  className?: string;
}

export const ApplicationSelector: React.FC<ApplicationSelectorProps> = ({
  forgeId,
  selectedApplicationId,
  onSelect,
  disabled = false,
  className = '',
}) => {
  const { t } = useTranslation();
  const [applications, setApplications] = useState<ForgeApplication[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchApplications = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const data = await forgeService.listApplications(forgeId || undefined);
        setApplications(data);
      } catch (err: any) {
        setError(err?.message || t('forge.selector.errorLoadingApplications', 'Failed to load applications'));
        console.error('Error fetching Forge applications:', err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchApplications();
  }, [forgeId, t]);

  const handleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    if (value === '') {
      onSelect(null, null);
    } else {
      const app = applications.find(a => a.app_id === value);
      onSelect(value, app || null);
    }
  };

  // Helper to get state badge color based on status
  const getStateBadge = (status?: string | null) => {
    if (!status) return '';
    switch (status.toLowerCase()) {
      case 'deployed':
      case 'running':
        return '🟢';
      case 'stopped':
        return '🔴';
      case 'deploying':
      case 'building':
      case 'creating':
      case 'initializing':
        return '🟡';
      case 'error':
      case 'build_failed':
        return '⚠️';
      default:
        return '⚪';
    }
  };

  return (
    <div className={className}>
      <label className="block text-sm font-medium text-muted-foreground mb-1">
        {t('forge.selector.applicationLabel', 'Forge Application')}
      </label>
      <select
        value={selectedApplicationId || ''}
        onChange={handleChange}
        disabled={disabled || isLoading}
        className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 disabled:opacity-50"
      >
        <option value="">
          {isLoading
            ? t('global.loading', 'Loading...')
            : t('forge.selector.selectApplication', '-- Select an Application --')}
        </option>
        {applications.map((app) => (
          <option key={app.app_id} value={app.app_id}>
            {getStateBadge(app.status)} {app.name}
          </option>
        ))}
      </select>
      {error && (
        <p className="text-xs text-destructive mt-1">{error}</p>
      )}
    </div>
  );
};

export default ApplicationSelector;
